import { useEffect, useRef, useState } from 'react';

import { useUpdateProjectMutation } from 'features/projects';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { usePostTestAlertsMutation } from '../services/alerts.api';
import { type AlertAction, type AlertType } from '../types/alertsConfig';
import type { ProjectDetail } from '../ui/project-detail.types';

/** Omite acciones cuya función quedó en blanco (no se persisten). */
const dropEmptyFunctionActions = (alert: AlertType): AlertType => {
  const kept = alert.actions?.filter((a) => a.function.trim() !== '');
  return {
    ...alert,
    actions: kept && kept.length > 0 ? kept : undefined,
  };
};

const sanitizeAlertsConfig = (alerts: AlertType[]): AlertType[] => alerts.map(dropEmptyFunctionActions);

const getCustomAlertsConfig = (alertsConfig: AlertType[] | null | undefined): AlertType[] =>
  (alertsConfig ?? []).filter((alert) => !alert.deletedAt && !alert.standard);

const buildAlertsDraft = (alertsConfig: AlertType[] | null | undefined, standardAlerts: AlertType[]): AlertType[] => [
  ...standardAlerts.map((standardAlert) => {
    const stored = alertsConfig?.find((alert) => alert.standard && !alert.deletedAt && alert.id === standardAlert.id);
    return { ...standardAlert, actions: stored?.actions ?? [], disabled: stored?.disabled ?? false };
  }),
  ...getCustomAlertsConfig(alertsConfig),
];

const getStoredStandardIds = (alertsConfig: AlertType[] | null | undefined): Set<string> =>
  new Set((alertsConfig ?? []).filter((alert) => alert.standard && !alert.deletedAt).map(({ id }) => id));

const toStoredAlert = (alert: AlertType): AlertType =>
  alert.standard
    ? {
        id: alert.id,
        description: '',
        examples: [],
        actions: alert.actions,
        standard: true,
        ...(alert.disabled ? { disabled: true } : {}),
      }
    : alert;

const createEmptyAlert = (): AlertType => ({
  id: '',
  description: '',
  examples: [],
  actions: [],
});

interface SaveBannerState {
  variant: 'success' | 'error';
  text: string;
}

interface TestAlertState {
  testText: string;
  result: string | null;
}

interface AlertsDraftState {
  alertsConfigDraft: AlertType[];
  selectedAlertIndex: number | null;
  testResults: TestAlertState[];
}

interface UseProjectAlertsConfigEditorStateArgs {
  project: ProjectDetail;
  standardAlerts: AlertType[];
}

interface UseProjectAlertsConfigEditorStateReturn {
  alertsConfigDraft: AlertType[];
  handleActionChange: (alertIndex: number, actionIndex: number, key: keyof AlertAction, value: string) => void;
  handleActionParameterChange: (
    alertIndex: number,
    actionIndex: number,
    paramKey: string,
    paramValue: string | number | boolean | null
  ) => void;
  handleAddAction: (alertIndex: number) => void;
  handleAddAlert: () => void;
  handleAddExample: (alertIndex: number) => void;
  handleAlertChange: (index: number, key: keyof AlertType, value: string) => void;
  handleDeleteAction: (alertIndex: number, actionIndex: number) => void;
  handleToggleStandardAlert: (alertIndex: number) => void;
  handleDeleteAlert: (index: number) => Promise<void>;
  handleDeleteExample: (alertIndex: number, exampleIndex: number) => void;
  handleExampleChange: (alertIndex: number, exampleIndex: number, value: string) => void;
  handleSaveAlertsConfig: () => Promise<void>;
  handleSelectAlert: (nextSelectedAlertIndex: number | null) => void;
  handleTestAlerts: (index: number) => Promise<void>;
  handleTestChange: (index: number, value: string) => void;
  isSaving: boolean;
  saveBanner: SaveBannerState | null;
  searchTerm: string;
  selectedAlert: AlertType | null;
  selectedAlertIndex: number | null;
  selectedTestState: TestAlertState | null;
  setSearchTerm: (value: string) => void;
}

const createAlertsDraftState = (
  alertsConfig: AlertType[] | null | undefined,
  standardAlerts: AlertType[]
): AlertsDraftState => {
  const alertsConfigDraft = buildAlertsDraft(alertsConfig, standardAlerts);
  return {
    alertsConfigDraft,
    selectedAlertIndex: alertsConfigDraft.length > 0 ? 0 : null,
    testResults: alertsConfigDraft.map(() => ({ testText: '', result: null })),
  };
};

const useProjectAlertsConfigEditorState = ({
  project,
  standardAlerts,
}: UseProjectAlertsConfigEditorStateArgs): UseProjectAlertsConfigEditorStateReturn => {
  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();
  const [postTestAlerts] = usePostTestAlertsMutation();

  const [draftState, setDraftState] = useState<AlertsDraftState>(() =>
    createAlertsDraftState(project.alertsConfig, standardAlerts)
  );
  const persistedAlertsConfig = useRef<AlertType[]>(getCustomAlertsConfig(project.alertsConfig));
  const storedStandardIds = useRef<Set<string>>(getStoredStandardIds(project.alertsConfig));
  const [searchTerm, setSearchTerm] = useState('');
  const [saveBanner, setSaveBanner] = useState<SaveBannerState | null>(null);

  useEffect(() => {
    if (!saveBanner) return;
    const timer = setTimeout(() => setSaveBanner(null), 4000);
    return () => clearTimeout(timer);
  }, [saveBanner]);

  const { alertsConfigDraft, selectedAlertIndex, testResults } = draftState;
  const selectedAlert = selectedAlertIndex === null ? null : alertsConfigDraft[selectedAlertIndex] ?? null;
  const selectedTestState = selectedAlertIndex === null ? null : testResults[selectedAlertIndex] ?? null;

  const handleSelectAlert = (nextSelectedAlertIndex: number | null): void => {
    setDraftState((prev) => ({
      ...prev,
      selectedAlertIndex: nextSelectedAlertIndex,
    }));
  };

  const handleAddAlert = (): void => {
    setSearchTerm('');
    setSaveBanner(null);
    setDraftState((prev) => {
      const nextAlertsConfigDraft = [...prev.alertsConfigDraft, createEmptyAlert()];
      return {
        alertsConfigDraft: nextAlertsConfigDraft,
        selectedAlertIndex: nextAlertsConfigDraft.length - 1,
        testResults: [...prev.testResults, { testText: '', result: null }],
      };
    });
  };

  const handleDeleteAlert = async (index: number): Promise<void> => {
    setSaveBanner(null);

    const alertToDelete = alertsConfigDraft[index];
    if (alertToDelete.standard) return;
    const persistedAlertIndex = persistedAlertsConfig.current.findIndex((alert) => alert.id === alertToDelete.id);

    if (persistedAlertIndex !== -1) {
      const nextPersistedAlertsConfig = persistedAlertsConfig.current.filter(
        (_, alertIndex) => alertIndex !== persistedAlertIndex
      );
      try {
        await updateProject({
          id: project.id,
          patch: { alertsConfig: nextPersistedAlertsConfig.length > 0 ? nextPersistedAlertsConfig : null },
        }).unwrap();
        persistedAlertsConfig.current = nextPersistedAlertsConfig;
        setSaveBanner({ variant: 'success', text: 'Alerta eliminada correctamente.' });
      } catch (error) {
        setSaveBanner({ variant: 'error', text: extractErrorMessage(error, 'No se pudo eliminar la alerta.') });
        return;
      }
    }

    setDraftState((prev) => {
      const nextAlertsConfigDraft = prev.alertsConfigDraft.filter((_, alertIndex) => alertIndex !== index);
      const nextTestResults = prev.testResults.filter((_, testIndex) => testIndex !== index);
      let nextSelectedAlertIndex = prev.selectedAlertIndex;

      if (nextAlertsConfigDraft.length === 0) {
        nextSelectedAlertIndex = null;
      } else if (prev.selectedAlertIndex === null || prev.selectedAlertIndex === index) {
        nextSelectedAlertIndex = Math.min(index, nextAlertsConfigDraft.length - 1);
      } else if (prev.selectedAlertIndex > index) {
        nextSelectedAlertIndex = prev.selectedAlertIndex - 1;
      }

      return {
        alertsConfigDraft: nextAlertsConfigDraft,
        selectedAlertIndex: nextSelectedAlertIndex,
        testResults: nextTestResults,
      };
    });
  };

  const handleAlertChange = (index: number, key: keyof AlertType, value: string): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, alertIndex) => {
        if (alertIndex !== index) {
          return alert;
        }

        return { ...alert, [key]: value };
      }),
    }));
  };

  const fetchTestAlerts = async (text: string, alertsConfig: AlertType[]): Promise<string> => {
    try {
      const response = await postTestAlerts({
        text,
        alertsConfig: sanitizeAlertsConfig(alertsConfig),
      }).unwrap();
      if (response.message) {
        return response.message;
      }
      return 'No message returned';
    } catch {
      return 'Error al probar la alerta.';
    }
  };

  const handleTestChange = (index: number, value: string): void => {
    setDraftState((prev) => ({
      ...prev,
      testResults: prev.testResults.map((testState, testIndex) => {
        if (testIndex !== index) {
          return testState;
        }

        return { ...testState, testText: value };
      }),
    }));
  };

  const handleTestAlerts = async (index: number): Promise<void> => {
    const testText = testResults[index]?.testText ?? '';

    if (!testText) {
      setDraftState((prev) => ({
        ...prev,
        testResults: prev.testResults.map((testState, testIndex) => {
          if (testIndex !== index) {
            return testState;
          }

          return { ...testState, result: 'Ingrese un texto para probar.' };
        }),
      }));
      return;
    }

    setDraftState((prev) => ({
      ...prev,
      testResults: prev.testResults.map((testState, testIndex) => {
        if (testIndex !== index) {
          return testState;
        }

        return { ...testState, result: '' };
      }),
    }));

    const testResponse = await fetchTestAlerts(
      testText,
      alertsConfigDraft.filter((alert) => !alert.standard)
    );
    setDraftState((prev) => ({
      ...prev,
      testResults: prev.testResults.map((testState, testIndex) => {
        if (testIndex !== index) {
          return testState;
        }

        return { ...testState, result: testResponse };
      }),
    }));
  };

  const handleAddExample = (alertIndex: number): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex) {
          return alert;
        }

        return {
          ...alert,
          examples: [...alert.examples, ''],
        };
      }),
    }));
  };

  const handleExampleChange = (alertIndex: number, exampleIndex: number, value: string): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex) {
          return alert;
        }

        return {
          ...alert,
          examples: alert.examples.map((example, currentExampleIndex) => {
            if (currentExampleIndex === exampleIndex) {
              return value;
            }

            return example;
          }),
        };
      }),
    }));
  };

  const handleDeleteExample = (alertIndex: number, exampleIndex: number): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex) {
          return alert;
        }

        return {
          ...alert,
          examples: alert.examples.filter((_, currentExampleIndex) => currentExampleIndex !== exampleIndex),
        };
      }),
    }));
  };

  const handleAddAction = (alertIndex: number): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex) {
          return alert;
        }

        return {
          ...alert,
          actions: [...(alert.actions || []), { function: '' }],
        };
      }),
    }));
  };

  const handleActionChange = (alertIndex: number, actionIndex: number, key: keyof AlertAction, value: string): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex || !alert.actions?.[actionIndex]) {
          return alert;
        }

        return {
          ...alert,
          actions: alert.actions.map((action, currentActionIndex) => {
            if (currentActionIndex !== actionIndex) {
              return action;
            }

            return {
              ...action,
              [key]: value,
            };
          }),
        };
      }),
    }));
  };

  const handleActionParameterChange = (
    alertIndex: number,
    actionIndex: number,
    paramKey: string,
    paramValue: string | number | boolean | null
  ): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex || !alert.actions?.[actionIndex]) {
          return alert;
        }

        return {
          ...alert,
          actions: alert.actions.map((action, currentActionIndex) => {
            if (currentActionIndex !== actionIndex) {
              return action;
            }

            const nextParams = { ...action.parameters };
            if (paramValue === null) {
              delete nextParams[paramKey];
            } else {
              nextParams[paramKey] = paramValue;
            }

            return {
              ...action,
              parameters: Object.keys(nextParams).length > 0 ? nextParams : undefined,
            };
          }),
        };
      }),
    }));
  };

  const handleDeleteAction = (alertIndex: number, actionIndex: number): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) => {
        if (index !== alertIndex) {
          return alert;
        }

        return {
          ...alert,
          actions: alert.actions?.filter((_, currentActionIndex) => currentActionIndex !== actionIndex) || [],
        };
      }),
    }));
  };

  const handleToggleStandardAlert = (alertIndex: number): void => {
    setSaveBanner(null);
    setDraftState((prev) => ({
      ...prev,
      alertsConfigDraft: prev.alertsConfigDraft.map((alert, index) =>
        index === alertIndex ? { ...alert, disabled: !alert.disabled } : alert
      ),
    }));
  };

  const handleSaveAlertsConfig = async (): Promise<void> => {
    const filteredAlertsConfig = alertsConfigDraft.reduce<AlertType[]>((acc, alert) => {
      if (!alert.id || !alert.description) {
        return acc;
      }

      const alertToSave = dropEmptyFunctionActions(alert);
      if (
        alertToSave.standard &&
        !alertToSave.disabled &&
        !alertToSave.actions?.length &&
        !storedStandardIds.current.has(alertToSave.id)
      ) {
        return acc;
      }

      acc.push(toStoredAlert(alertToSave));
      return acc;
    }, []);

    try {
      await updateProject({
        id: project.id,
        patch: { alertsConfig: filteredAlertsConfig.length > 0 ? filteredAlertsConfig : null },
      }).unwrap();
      persistedAlertsConfig.current = filteredAlertsConfig;
      storedStandardIds.current = getStoredStandardIds(filteredAlertsConfig);
      setSaveBanner({ variant: 'success', text: 'Alertas guardadas correctamente.' });
    } catch (error) {
      setSaveBanner({ variant: 'error', text: extractErrorMessage(error, 'No se pudieron guardar las alertas.') });
    }
  };

  return {
    alertsConfigDraft,
    handleActionChange,
    handleActionParameterChange,
    handleAddAction,
    handleAddAlert,
    handleAddExample,
    handleAlertChange,
    handleDeleteAction,
    handleToggleStandardAlert,
    handleDeleteAlert,
    handleDeleteExample,
    handleExampleChange,
    handleSaveAlertsConfig,
    handleSelectAlert,
    handleTestAlerts,
    handleTestChange,
    isSaving,
    saveBanner,
    searchTerm,
    selectedAlert,
    selectedAlertIndex,
    selectedTestState,
    setSearchTerm,
  };
};

export type { AlertsDraftState, SaveBannerState, TestAlertState, UseProjectAlertsConfigEditorStateReturn };

export default useProjectAlertsConfigEditorState;
